import event from '../../../utils/analytics/event';
/**
 * Custom hook which provides email sharing functionality anywhere within the app.
 *
 * By default, it logs 1 piwik event: `event(['share', 'email', <PAGE_BEING_SHARED>]);`, but you can pass in a custom `piwikEvent` to log instead.
 *
 * This event is logged when the user begins to share with email. If choosing to continue with the default piwik events provided, a value must be provided for the `page` prop.
 *
 * If a value is passed for the `piwikEvent` prop, then the button onClick will log that custom event instead.
 *
 * ```js
 * import { useEmailShare } from '@minerva-analytics/pantheon';
 *
 * // Without custom piwik event
 * const Test = () => {
 *   const emailShare = useEmailShare(
 *     'Check out this awesome article!',
 *     'Check out this article: https://article.com',
 *     'article-page',
 *   );
 *   return (
 *     <button onClick={() => emailShare()}>
 *       Email Button
 *     </button>
 *   );
 * }
 *
 * // With custom piwik event
 * const Test = () => {
 *   const emailShare = useEmailShare(
 *     'Check out this awesome article!',
 *     'Check out this article: https://article.com',
 *     ['click', 'email-share'],
 *   );
 *   return (
 *     <button onClick={() => emailShare()}>
 *       Email Button
 *     </button>
 *   );
 * }
 * ```
 *
 * @param {string} subject - Custom email subject
 * @param {string} body - Custom email body
 * @param {string} page - The page that this hook will be used on. This is used for creating unique piwik events per page.
 * @param {array} piwikEvent - Optional custom event that is logged upon sharing with email. Must be an array in the form of `[category, action, name, value]`, though `name` and `value` are optional.
 *
 * @returns {function} - Function that allows user to share with Email
 */

/* eslint-disable-next-line*/

var useEmailShare = function useEmailShare(subject, body, page, piwikEvent) {
  if (piwikEvent === void 0) {
    piwikEvent = null;
  }

  var share = function share() {
    event(piwikEvent || ['share', 'email', page]);
    window.open("mailto:?subject=" + subject + "&body=" + encodeURIComponent(body));
  };

  return share;
};

export default useEmailShare;